import * as React from 'react'
import { Outlet, useOutletContext } from 'react-router-dom'

import { Box, Button, Section, useToggle } from '@factorialco/deprecated-design-system'

import { observer } from 'mobx-react'

import BoxList from 'modules/core/components/BoxList'
import Popover from 'modules/core/components/Popover'
import type { Option } from 'modules/core/components/Popover/Content'
import PopoverContent from 'modules/core/components/Popover/Content'
import createComputed from 'modules/core/hooks/createComputed'
import { useAsyncData } from 'modules/core/hooks/useAsyncData'
import i18n from 'modules/core/lib/i18n'
import collectionUsers from 'modules/core/store/collections/users'
import type User from 'modules/core/store/models/User'

import contractVersions from 'modules/contracts/store/collections/contractVersions'
import { ResponsivePage } from 'modules/layout/components/ResponsivePage'
import EditUserModal from 'modules/permissions/components/EditUserModal'
import { PermissionGroupsLayout } from 'modules/permissions/components/PermissionGroupsLayout'
import AssignUsersModal from 'modules/permissions/components/Permissions/AssignUsersModal'

import { PermissionGroupContainerProps } from '../PermissionGroupContainer'
import Item from './Item'
import styles from './index.module.scss'

const Externals = observer(() => {
  const { permissionGroup } = useOutletContext<PermissionGroupContainerProps>()

  const [isEditUserModalOpen, setIsEditUserModalOpen] = React.useState(false)
  const [isEmployeesModalOpen, setIsEmployeesModalOpen] = React.useState(false)
  const [open, toggle] = useToggle(false)

  const { loading } = useAsyncData(
    () => contractVersions.fetch({ withCachedFetchParams: true }),
    []
  )

  const useUsers = createComputed((users: User[]): User[] =>
    users.filter((user) => user.attributes.affiliation === 'external')
  )

  const users = useUsers(permissionGroup.users).get()

  const assignableUsers = (): User[] => {
    return collectionUsers.filter((user) => user.attributes.affiliation === 'external')
  }

  const description = (): string => {
    return i18n.t('settings.permissions.group.employees_section.external_group.description', {
      group: permissionGroup.attributes.name,
    })
  }

  const options = (): Option[] => {
    const allowedOptions = []

    if (collectionUsers.current.policy.can('permissions.groupAssignEmployees')) {
      allowedOptions.push({
        onClick: onOpenEmployeesModal,
        title: i18n.t('settings.permissions.group.actions.assign_users.title'),
        description: i18n.t('settings.permissions.group.actions.assign_users.description'),
      })
    }

    if (collectionUsers.current.policy.can('permissions.groupCreateExternalUsers')) {
      allowedOptions.push({
        onClick: openAdminModal,
        title: i18n.t('settings.permissions.group.actions.new_user.title'),
        description: i18n.t('settings.permissions.group.actions.new_user.description'),
      })
    }

    return allowedOptions
  }

  function onOpenEmployeesModal() {
    setIsEmployeesModalOpen(true)
  }

  function closeEmpleesModal() {
    setIsEmployeesModalOpen(false)
  }

  function openAdminModal() {
    setIsEditUserModalOpen(true)
  }

  function closeAdminModal() {
    setIsEditUserModalOpen(false)
  }

  function renderList() {
    if (!users.length) return <div className={styles.blankSlate} />

    return (
      <BoxList>
        {users.map((user) => (
          <Item key={user.attributes.id} permissionGroup={permissionGroup} user={user} />
        ))}
      </BoxList>
    )
  }

  return (
    <ResponsivePage.Body showHeader={false} loading={loading} error={null}>
      <PermissionGroupsLayout />
      <Section.FullWidth
        illustration="laptop-user"
        title={permissionGroup.title}
        description={description()}
      >
        {options().length && (
          <Box alignItems="flexEnd" paddingBottom="s24">
            <Popover
              attachment="top right"
              targetAttachment="bottom center"
              toggle={toggle}
              open={open}
            >
              <Button onClick={toggle(true)} iconName="add" />
              <PopoverContent onClose={toggle(false)} options={options()} />
            </Popover>
          </Box>
        )}
        {isEditUserModalOpen && (
          <EditUserModal onClose={() => closeAdminModal()} permissionGroup={permissionGroup} />
        )}
        {isEmployeesModalOpen && (
          <AssignUsersModal
            onClose={() => closeEmpleesModal()}
            permissionGroup={permissionGroup}
            users={assignableUsers()}
            contractVersions={contractVersions}
          />
        )}
        <>
          {renderList()}
          <Outlet />
        </>
      </Section.FullWidth>
    </ResponsivePage.Body>
  )
})

export default Externals
